import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box} from "@mui/system";
import {Dialog, DialogActions, DialogTitle, Typography} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import ButtonAdd from "../../../components/UI/button/ButtonAdd";
import ButtonExit from "../../../components/UI/button/ButtonExit";
import CompositionController from "../../../API/organization/CompositionController";
import {
    getCompositionImageUrl,
    getCompositionThumbnailUrl,
    getSortedCompositionPictures,
} from "../../../utils/getCompositionPictures";
import {
    cancelMediaMarkersRender,
    catalogItemGalleryStackMediaQuery,
    catalogItemGalleryStackMq,
    catalogItemMediaStackMq,
    catalogItemNarrowMq,
    collectMediaModuleMarkers,
    MEDIA_IMAGE_DEFAULT_HEIGHT,
    MEDIA_IMAGE_DEFAULT_WIDTH,
    MEDIA_MODULE_MARKER_CORE_SIZE,
    MEDIA_MODULE_MARKER_RING_WIDTH,
    MEDIA_MODULE_MARKER_SIZE,
    normalizeModuleCompositionsForMedia,
    scheduleMediaMarkersRender,
} from "../../../utils/compositionMediaUtils";
import {buildOrganizationCompositionModulePatchBody} from "../../../utils/mapModuleCompositionToModuleItem";
import {
    COMPOSITION_MEDIA_GALLERY_GAP,
    COMPOSITION_MEDIA_GALLERY_MARGIN_BOTTOM,
    COMPOSITION_MEDIA_IMAGE_ASPECT_RATIO,
    COMPOSITION_MEDIA_IMAGE_MAX_WIDTH,
    COMPOSITION_MEDIA_THUMBNAIL_SIZE_COMPACT,
    compositionMediaInfoCardMeasuredSx,
    compositionMediaModulePanelInCardSx,
} from "../../../components/composition/compositionMediaLayoutSx";
import {CompositionThumbnailsCarousel} from "../../../components/composition/CompositionThumbnailsCarousel";
import {
    CompositionMediaModulePanel,
    useCompositionMediaHover,
} from "../../../components/composition/CompositionMediaView";

const settingsMediaRowIdPrefix = "settings-media-module-row-";

const settingsGalleryRowSx = {
    display: "flex",
    flexWrap: "nowrap",
    gap: `${COMPOSITION_MEDIA_GALLERY_GAP}px`,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    alignItems: "flex-start",
    overflow: "hidden",
    marginBottom: `${COMPOSITION_MEDIA_GALLERY_MARGIN_BOTTOM}px`,
    [catalogItemGalleryStackMq]: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "auto",
        columnGap: "12px",
        alignItems: "start",
    },
    [catalogItemNarrowMq]: {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        alignItems: "stretch",
    },
};

const settingsMediaStackSx = {
    display: "contents",
    [catalogItemGalleryStackMq]: {
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        gridColumn: 1,
        gridRow: 1,
        minWidth: 0,
        width: "100%",
    },
};

const settingsInfoColumnSx = {
    flex: "1 1 0",
    minWidth: 0,
    maxWidth: COMPOSITION_MEDIA_IMAGE_MAX_WIDTH,
    width: "100%",
    display: "flex",
    gap: `${COMPOSITION_MEDIA_GALLERY_GAP}px`,
    flexDirection: "column",
    [catalogItemMediaStackMq]: {
        gap: "16px",
    },
    [catalogItemGalleryStackMq]: {
        gridColumn: 2,
        gridRow: 1,
        alignSelf: "start",
        maxWidth: "none",
        minHeight: 0,
    },
    [catalogItemNarrowMq]: {
        minWidth: "100%",
        width: "100%",
    },
};

const settingsThumbnailItemSx = {
    width: "112px",
    height: "112px",
    boxSizing: "border-box",
    flexShrink: 0,
    borderRadius: "16px",
    overflow: "hidden",
};

const settingsImageClipSx = {
    position: "absolute",
    inset: 0,
    borderRadius: "inherit",
    overflow: "hidden",
};

const settingsImageBoxSx = {
    position: "relative",
    flex: "1 1 0",
    minWidth: 0,
    maxWidth: COMPOSITION_MEDIA_IMAGE_MAX_WIDTH,
    aspectRatio: COMPOSITION_MEDIA_IMAGE_ASPECT_RATIO,
    alignSelf: "flex-start",
    borderRadius: "16px",
    overflow: "visible",
    boxSizing: "border-box",
    border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
    backgroundColor: (theme) => theme.palette.background.grey,
    [catalogItemGalleryStackMq]: {
        order: 1,
        flex: "0 0 auto",
        alignSelf: "stretch",
        width: "100%",
        maxWidth: "100%",
    },
};

const settingsInfoCardSx = {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
    borderRadius: "16px",
    overflow: "hidden",
    flex: "0 0 auto",
};

const areMediaCoordinatesEqual = (left, right) =>
    (left?.x ?? null) === (right?.x ?? null) &&
    (left?.y ?? null) === (right?.y ?? null);

const clampMediaCoordinateValue = (field, value, imageSize) => {
    if (value == null || value === "") {
        return null;
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
        return null;
    }
    const max = field === "coordinateXPoint" ? imageSize.width : imageSize.height;
    return Math.round(Math.min(max, Math.max(0, num)));
};

const SettingsMediaModuleMarker = React.memo(function SettingsMediaModuleMarker({
    moduleRow,
    isMarkerActive,
    isDragging,
    onMouseEnter,
    onMouseLeave,
    onMouseDown,
}) {
    const x = Number(moduleRow.coordinateXPoint);
    const y = Number(moduleRow.coordinateYPoint);
    const left =
        !Number.isNaN(x) && MEDIA_IMAGE_DEFAULT_WIDTH > 0
            ? `${(x / MEDIA_IMAGE_DEFAULT_WIDTH) * 100}%`
            : `${moduleRow.coordinateXPoint}px`;
    const top =
        !Number.isNaN(y) && MEDIA_IMAGE_DEFAULT_HEIGHT > 0
            ? `${(y / MEDIA_IMAGE_DEFAULT_HEIGHT) * 100}%`
            : `${moduleRow.coordinateYPoint}px`;

    const priorityLabel =
        moduleRow.priority != null && moduleRow.priority !== ""
            ? String(moduleRow.priority)
            : null;

    return (
        <Box
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onMouseDown={onMouseDown}
            sx={{
                position: "absolute",
                left,
                top,
                transform: "translate(-50%, -50%)",
                width: `${MEDIA_MODULE_MARKER_SIZE}px`,
                height: `${MEDIA_MODULE_MARKER_SIZE}px`,
                cursor: isDragging ? "grabbing" : "pointer",
                zIndex: isMarkerActive ? 2 : 1,
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    boxSizing: "border-box",
                    borderRadius: "50%",
                    backgroundColor: "transparent",
                    border: `${MEDIA_MODULE_MARKER_RING_WIDTH}px solid rgba(59, 139, 236, ${isMarkerActive ? 0.3 : 0.1})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Box
                    sx={{
                        width: `${MEDIA_MODULE_MARKER_CORE_SIZE}px`,
                        height: `${MEDIA_MODULE_MARKER_CORE_SIZE}px`,
                        borderRadius: "50%",
                        backgroundColor: "#FFFFFF",
                        flexShrink: 0,
                    }}
                />
            </Box>
            {priorityLabel != null ? (
                <Box
                    sx={{
                        position: "absolute",
                        top: -4,
                        right: -6,
                        minWidth: priorityLabel.length > 1 ? "22px" : "18px",
                        height: "18px",
                        px: priorityLabel.length > 1 ? "4px" : 0,
                        borderRadius: priorityLabel.length > 1 ? "9px" : "50%",
                        backgroundColor: (theme) => theme.palette.primary.dark,
                        border: "1px solid #FFFFFF",
                        boxSizing: "border-box",
                        pointerEvents: "none",
                        display: "grid",
                        placeItems: "center",
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            color: "#FFFFFF",
                            fontSize: "12px",
                            fontWeight: 400,
                            lineHeight: 1,
                            fontFamily: (theme) => theme.typography.fontFamily,
                            fontVariantNumeric: "tabular-nums",
                            m: 0,
                            p: 0,
                        }}
                    >
                        {priorityLabel}
                    </Typography>
                </Box>
            ) : null}
        </Box>
    );
});

/**
 * Media tab for organization composition settings.
 * Visual layout mirrors /catalog/:id, but stays a separate component.
 */
const CompositionSettingsMedia = ({
    compositionId,
    data,
    moduleCompositions,
    setModuleCompositions,
    handleModalAlert,
}) => {
    const [selectedPictureId, setSelectedPictureId] = useState(null);
    const [draggingModuleRowId, setDraggingModuleRowId] = useState(null);
    const [markersReady, setMarkersReady] = useState(false);
    const [coordinateConfirm, setCoordinateConfirm] = useState(null);
    const [coordinateConfirmLoading, setCoordinateConfirmLoading] = useState(false);
    const [imageBoxHeight, setImageBoxHeight] = useState(null);
    const [mediaStackHeight, setMediaStackHeight] = useState(null);

    const imageContainerRef = useRef(null);
    const mediaStackRef = useRef(null);
    const mainImageRef = useRef(null);
    const markersRenderTaskRef = useRef(null);
    const moduleCompositionsRef = useRef(moduleCompositions);
    const dragCoordsRef = useRef({x: 0, y: 0});
    const dragStartCoordsRef = useRef(null);
    const draggingModuleRowIdRef = useRef(null);
    const coordinateEditStartRef = useRef(null);
    const coordinateBlurTimeoutRef = useRef(null);
    const imageSizeRef = useRef({
        width: MEDIA_IMAGE_DEFAULT_WIDTH,
        height: MEDIA_IMAGE_DEFAULT_HEIGHT,
    });

    const isGalleryStacked = useMediaQuery(catalogItemGalleryStackMediaQuery);
    const {
        hoveredModuleRowId,
        setModuleRowHover,
        clearModuleRowHover,
    } = useCompositionMediaHover();

    const pictures = useMemo(
        () => getSortedCompositionPictures(data),
        [data],
    );
    const selectedPicture = useMemo(
        () => pictures.find((p) => p.id === selectedPictureId) ?? pictures[0],
        [pictures, selectedPictureId],
    );
    const mainImageUrl = useMemo(
        () => getCompositionImageUrl(data, selectedPicture),
        [data, selectedPicture],
    );
    const firstPicture = pictures[0] ?? null;
    const isFirstPictureSelected =
        firstPicture != null && selectedPicture?.id === firstPicture.id;

    const mediaModules = useMemo(
        () => normalizeModuleCompositionsForMedia(moduleCompositions),
        [moduleCompositions],
    );

    const markers = useMemo(() => {
        if (!isFirstPictureSelected || !markersReady) {
            return [];
        }
        return collectMediaModuleMarkers(mediaModules);
    }, [mediaModules, isFirstPictureSelected, markersReady]);

    moduleCompositionsRef.current = moduleCompositions;
    draggingModuleRowIdRef.current = draggingModuleRowId;

    useEffect(() => {
        setSelectedPictureId(pictures[0]?.id ?? null);
    }, [data.id, data.pictures]);

    const resetMarkersRender = useCallback(() => {
        if (markersRenderTaskRef.current != null) {
            cancelMediaMarkersRender(markersRenderTaskRef.current);
            markersRenderTaskRef.current = null;
        }
        setMarkersReady(false);
    }, []);

    const queueMarkersRender = useCallback(() => {
        if (markersRenderTaskRef.current != null) {
            cancelMediaMarkersRender(markersRenderTaskRef.current);
        }
        markersRenderTaskRef.current = scheduleMediaMarkersRender(() => {
            markersRenderTaskRef.current = null;
            setMarkersReady(true);
        });
    }, []);

    const handleMainImageLoad = useCallback(() => {
        if (!isFirstPictureSelected) {
            return;
        }
        queueMarkersRender();
    }, [isFirstPictureSelected, queueMarkersRender]);

    useEffect(() => {
        resetMarkersRender();
        if (!isFirstPictureSelected || !mainImageUrl) {
            return undefined;
        }
        const img = mainImageRef.current;
        if (img?.complete) {
            queueMarkersRender();
        }
        return () => {
            if (markersRenderTaskRef.current != null) {
                cancelMediaMarkersRender(markersRenderTaskRef.current);
                markersRenderTaskRef.current = null;
            }
        };
    }, [
        mainImageUrl,
        isFirstPictureSelected,
        queueMarkersRender,
        resetMarkersRender,
    ]);

    useEffect(() => {
        if (!isFirstPictureSelected) {
            setDraggingModuleRowId(null);
        }
    }, [isFirstPictureSelected]);

    const updateLayoutHeights = useCallback(() => {
        if (isGalleryStacked) {
            const mediaStackEl = mediaStackRef.current;
            if (!mediaStackEl) {
                setMediaStackHeight(null);
                setImageBoxHeight(null);
                return;
            }
            const stackHeight = Math.round(mediaStackEl.getBoundingClientRect().height);
            if (stackHeight <= 0) {
                return;
            }
            setMediaStackHeight(stackHeight);
            setImageBoxHeight(null);
            return;
        }
        const container = imageContainerRef.current;
        if (!container) {
            setMediaStackHeight(null);
            setImageBoxHeight(null);
            return;
        }
        const roundedHeight = Math.round(container.getBoundingClientRect().height);
        if (roundedHeight <= 0) {
            return;
        }
        setImageBoxHeight(roundedHeight);
        setMediaStackHeight(null);
    }, [isGalleryStacked]);

    useEffect(() => {
        let resizeObserver = null;
        let rafId = null;

        const connectLayoutObserver = () => {
            const layoutEl = isGalleryStacked
                ? mediaStackRef.current
                : imageContainerRef.current;
            if (!layoutEl) {
                return false;
            }
            updateLayoutHeights();
            resizeObserver = new ResizeObserver(updateLayoutHeights);
            resizeObserver.observe(layoutEl);
            if (isGalleryStacked && imageContainerRef.current) {
                resizeObserver.observe(imageContainerRef.current);
            }
            return true;
        };

        if (!connectLayoutObserver()) {
            rafId = requestAnimationFrame(() => {
                connectLayoutObserver();
            });
        }

        return () => {
            if (rafId != null) {
                cancelAnimationFrame(rafId);
            }
            resizeObserver?.disconnect();
        };
    }, [isGalleryStacked, mainImageUrl, updateLayoutHeights]);

    const cardTargetHeight = isGalleryStacked ? mediaStackHeight : imageBoxHeight;
    const useMeasuredCardHeight = cardTargetHeight != null;
    const infoColumnMeasuredSx =
        useMeasuredCardHeight && isGalleryStacked && mediaStackHeight != null
            ? {
                height: `${mediaStackHeight}px`,
                minHeight: `${mediaStackHeight}px`,
                maxHeight: `${mediaStackHeight}px`,
            }
            : null;

    const getImageCoordinates = useCallback((clientX, clientY) => {
        const container = imageContainerRef.current;
        if (!container) {
            return null;
        }
        const rect = container.getBoundingClientRect();
        const x = Math.round(
            Math.min(
                MEDIA_IMAGE_DEFAULT_WIDTH,
                Math.max(0, ((clientX - rect.left) / rect.width) * MEDIA_IMAGE_DEFAULT_WIDTH),
            ),
        );
        const y = Math.round(
            Math.min(
                MEDIA_IMAGE_DEFAULT_HEIGHT,
                Math.max(0, ((clientY - rect.top) / rect.height) * MEDIA_IMAGE_DEFAULT_HEIGHT),
            ),
        );
        return {x, y};
    }, []);

    const revertModuleCoordinates = useCallback((rowId, coords) => {
        setModuleCompositions((prev) =>
            prev.map((row) =>
                row.id === rowId
                    ? {
                        ...row,
                        coordinateXPoint: coords.x,
                        coordinateYPoint: coords.y,
                    }
                    : row,
            ),
        );
    }, [setModuleCompositions]);

    const handleCoordinateConfirmCancel = useCallback(() => {
        setCoordinateConfirm((pending) => {
            if (pending) {
                revertModuleCoordinates(pending.rowId, pending.previous);
            }
            return null;
        });
        coordinateEditStartRef.current = null;
    }, [revertModuleCoordinates]);

    const queueCoordinateConfirm = useCallback((rowId, previous, next) => {
        if (areMediaCoordinatesEqual(previous, next)) {
            coordinateEditStartRef.current = null;
            return;
        }
        const row = moduleCompositionsRef.current.find((r) => r.id === rowId);
        if (row?.id == null) {
            revertModuleCoordinates(rowId, previous);
            coordinateEditStartRef.current = null;
            return;
        }
        setCoordinateConfirm({rowId, previous, next});
        coordinateEditStartRef.current = null;
    }, [revertModuleCoordinates]);

    const handleCoordinateChange = useCallback((rowId, field, rawValue) => {
        if (coordinateConfirm) {
            return;
        }
        if (rawValue === "") {
            setModuleCompositions((prev) =>
                prev.map((row) => (row.id === rowId ? {...row, [field]: null} : row)),
            );
            return;
        }
        const value = clampMediaCoordinateValue(field, rawValue, imageSizeRef.current);
        setModuleCompositions((prev) =>
            prev.map((row) => (row.id === rowId ? {...row, [field]: value} : row)),
        );
    }, [coordinateConfirm, setModuleCompositions]);

    const handleCoordinateFocus = useCallback((row) => {
        if (coordinateConfirm) {
            return;
        }
        if (coordinateBlurTimeoutRef.current) {
            clearTimeout(coordinateBlurTimeoutRef.current);
            coordinateBlurTimeoutRef.current = null;
        }
        if (coordinateEditStartRef.current?.rowId !== row.id) {
            coordinateEditStartRef.current = {
                rowId: row.id,
                x: row.coordinateXPoint ?? null,
                y: row.coordinateYPoint ?? null,
            };
        }
    }, [coordinateConfirm]);

    const commitCoordinateEdit = useCallback((rowId) => {
        const row = moduleCompositionsRef.current.find((r) => r.id === rowId);
        if (!row?.module?.id) {
            coordinateEditStartRef.current = null;
            return;
        }

        const editStart = coordinateEditStartRef.current;
        const previous = editStart?.rowId === rowId
            ? {x: editStart.x, y: editStart.y}
            : {
                x: row.coordinateXPoint ?? null,
                y: row.coordinateYPoint ?? null,
            };

        const imageSize = imageSizeRef.current;
        const next = {
            x: clampMediaCoordinateValue(
                "coordinateXPoint",
                row.coordinateXPoint,
                imageSize,
            ),
            y: clampMediaCoordinateValue(
                "coordinateYPoint",
                row.coordinateYPoint,
                imageSize,
            ),
        };

        if (
            next.x !== row.coordinateXPoint ||
            next.y !== row.coordinateYPoint
        ) {
            setModuleCompositions((prev) =>
                prev.map((r) =>
                    r.id === rowId
                        ? {...r, coordinateXPoint: next.x, coordinateYPoint: next.y}
                        : r,
                ),
            );
        }

        queueCoordinateConfirm(rowId, previous, next);
    }, [queueCoordinateConfirm, setModuleCompositions]);

    const handleCoordinateBlur = useCallback((rowId) => {
        if (coordinateConfirm) {
            return;
        }
        if (coordinateBlurTimeoutRef.current) {
            clearTimeout(coordinateBlurTimeoutRef.current);
        }
        coordinateBlurTimeoutRef.current = window.setTimeout(() => {
            coordinateBlurTimeoutRef.current = null;
            commitCoordinateEdit(rowId);
        }, 0);
    }, [commitCoordinateEdit, coordinateConfirm]);

    useEffect(() => () => {
        if (coordinateBlurTimeoutRef.current) {
            clearTimeout(coordinateBlurTimeoutRef.current);
        }
    }, []);

    const handleCoordinateConfirmSubmit = useCallback(() => {
        if (coordinateConfirm?.rowId == null) {
            handleCoordinateConfirmCancel();
            return;
        }
        const {rowId, next, previous} = coordinateConfirm;
        const compositionModule = moduleCompositionsRef.current.find((r) => r.id === rowId);
        if (!compositionModule) {
            handleCoordinateConfirmCancel();
            return;
        }
        setCoordinateConfirmLoading(true);
        const moduleId = compositionModule.module?.id ?? compositionModule.moduleId;
        if (moduleId == null) {
            handleCoordinateConfirmCancel();
            handleModalAlert({message: "Не удалось определить модуль для сохранения координат"});
            return;
        }
        const patchBody = buildOrganizationCompositionModulePatchBody(compositionModule, {
            coordinateXPoint: next.x,
            coordinateYPoint: next.y,
        });
        if (patchBody == null) {
            handleCoordinateConfirmCancel();
            handleModalAlert({message: "Не удалось собрать тело запроса"});
            return;
        }
        CompositionController.module(
            compositionId,
            moduleId,
            patchBody,
        )
            .then(function (response) {
                setModuleCompositions((prev) =>
                    prev.map((r) =>
                        r.id === rowId
                            ? {
                                ...r,
                                coordinateXPoint:
                                    response.data?.coordinateXPoint ??
                                    next.x ??
                                    r.coordinateXPoint,
                                coordinateYPoint:
                                    response.data?.coordinateYPoint ??
                                    next.y ??
                                    r.coordinateYPoint,
                            }
                            : r,
                    ),
                );
                setCoordinateConfirm(null);
            })
            .catch(function (error) {
                revertModuleCoordinates(rowId, previous);
                handleModalAlert(error);
                setCoordinateConfirm(null);
            })
            .finally(function () {
                setCoordinateConfirmLoading(false);
            });
    }, [
        compositionId,
        coordinateConfirm,
        handleCoordinateConfirmCancel,
        handleModalAlert,
        revertModuleCoordinates,
        setModuleCompositions,
    ]);

    useEffect(() => {
        if (draggingModuleRowId == null) {
            return undefined;
        }

        const handleMouseMove = (event) => {
            const coords = getImageCoordinates(event.clientX, event.clientY);
            if (!coords) {
                return;
            }
            dragCoordsRef.current = coords;
            setModuleCompositions((prev) =>
                prev.map((row) =>
                    row.id === draggingModuleRowId
                        ? {
                            ...row,
                            coordinateXPoint: coords.x,
                            coordinateYPoint: coords.y,
                        }
                        : row,
                ),
            );
        };

        const handleMouseUp = () => {
            const rowId = draggingModuleRowId;
            const {x, y} = dragCoordsRef.current;
            const startCoords = dragStartCoordsRef.current;
            setDraggingModuleRowId(null);
            dragStartCoordsRef.current = null;

            if (!rowId || !startCoords || startCoords.rowId !== rowId) {
                return;
            }
            if (startCoords.x === x && startCoords.y === y) {
                return;
            }

            queueCoordinateConfirm(
                rowId,
                {x: startCoords.x, y: startCoords.y},
                {x, y},
            );
        };

        document.body.style.userSelect = "none";
        document.body.style.cursor = "grabbing";
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [draggingModuleRowId, getImageCoordinates, queueCoordinateConfirm, setModuleCompositions]);

    const clearHoverUnlessDragging = useCallback((rowId) => {
        if (draggingModuleRowIdRef.current === rowId) {
            return;
        }
        clearModuleRowHover(rowId);
    }, [clearModuleRowHover]);

    return (
        <>
            <Box sx={settingsGalleryRowSx}>
                <Box ref={mediaStackRef} sx={settingsMediaStackSx}>
                    <CompositionThumbnailsCarousel
                        pictures={pictures}
                        selectedPictureId={selectedPicture?.id}
                        onSelectPicture={setSelectedPictureId}
                        getThumbnailUrl={getCompositionThumbnailUrl}
                        maxHeight={
                            !isGalleryStacked && imageBoxHeight != null
                                ? imageBoxHeight
                                : undefined
                        }
                        thumbnailItemSx={settingsThumbnailItemSx}
                        thumbnailRailHeight={COMPOSITION_MEDIA_THUMBNAIL_SIZE_COMPACT}
                        emptyContent={(
                            <Box
                                sx={{
                                    ...settingsThumbnailItemSx,
                                    border: (theme) => `1px dashed ${theme.palette.primary.stroke}`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    px: 1,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "14px",
                                        color: "text.light",
                                        textAlign: "center",
                                    }}
                                >
                                    Нет изображений
                                </Typography>
                            </Box>
                        )}
                    />
                    <Box ref={imageContainerRef} sx={settingsImageBoxSx}>
                        <Box sx={settingsImageClipSx}>
                            {mainImageUrl ? (
                                <Box
                                    component="img"
                                    ref={mainImageRef}
                                    src={mainImageUrl}
                                    alt=""
                                    onLoad={() => {
                                        handleMainImageLoad();
                                        updateLayoutHeights();
                                    }}
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        objectPosition: "center",
                                        display: "block",
                                    }}
                                />
                            ) : null}
                        </Box>
                        {markers.map((moduleRow) => (
                            <SettingsMediaModuleMarker
                                key={moduleRow.id}
                                moduleRow={moduleRow}
                                isMarkerActive={
                                    hoveredModuleRowId === moduleRow.id ||
                                    draggingModuleRowId === moduleRow.id
                                }
                                isDragging={draggingModuleRowId === moduleRow.id}
                                onMouseEnter={() => {
                                    setModuleRowHover(moduleRow.id);
                                    document
                                        .getElementById(`${settingsMediaRowIdPrefix}${moduleRow.id}`)
                                        ?.scrollIntoView({block: "nearest", behavior: "smooth"});
                                }}
                                onMouseLeave={() => clearHoverUnlessDragging(moduleRow.id)}
                                onMouseDown={(event) => {
                                    if (coordinateConfirm) {
                                        return;
                                    }
                                    event.preventDefault();
                                    dragStartCoordsRef.current = {
                                        rowId: moduleRow.id,
                                        x: moduleRow.coordinateXPoint,
                                        y: moduleRow.coordinateYPoint,
                                    };
                                    const coords = getImageCoordinates(event.clientX, event.clientY);
                                    if (coords) {
                                        dragCoordsRef.current = coords;
                                        setModuleCompositions((prev) =>
                                            prev.map((row) =>
                                                row.id === moduleRow.id
                                                    ? {
                                                        ...row,
                                                        coordinateXPoint: coords.x,
                                                        coordinateYPoint: coords.y,
                                                    }
                                                    : row,
                                            ),
                                        );
                                    }
                                    setDraggingModuleRowId(moduleRow.id);
                                    setModuleRowHover(moduleRow.id);
                                }}
                            />
                        ))}
                    </Box>
                </Box>
                <Box
                    sx={{
                        ...settingsInfoColumnSx,
                        ...infoColumnMeasuredSx,
                        [catalogItemNarrowMq]: {
                            height: "auto",
                            minHeight: "auto",
                            maxHeight: "none",
                        },
                    }}
                >
                    <Box
                        sx={{
                            ...settingsInfoCardSx,
                            ...(useMeasuredCardHeight
                                ? {
                                    minHeight: `${cardTargetHeight}px`,
                                    height: `${cardTargetHeight}px`,
                                    maxHeight: `${cardTargetHeight}px`,
                                    ...compositionMediaInfoCardMeasuredSx,
                                }
                                : null),
                            [catalogItemNarrowMq]: {
                                height: "auto",
                                minHeight: "auto",
                                maxHeight: "none",
                            },
                        }}
                    >
                        <CompositionMediaModulePanel
                            moduleCompositions={mediaModules}
                            cornerType={data?.cornerType}
                            rowIdPrefix={settingsMediaRowIdPrefix}
                            hoveredModuleRowId={hoveredModuleRowId}
                            setModuleRowHover={setModuleRowHover}
                            clearModuleRowHover={clearHoverUnlessDragging}
                            showCoordinates
                            linkModuleName
                            coordinateInputsDisabled={Boolean(coordinateConfirm)}
                            coordinateMaxX={MEDIA_IMAGE_DEFAULT_WIDTH}
                            coordinateMaxY={MEDIA_IMAGE_DEFAULT_HEIGHT}
                            onCoordinateChange={handleCoordinateChange}
                            onCoordinateFocus={handleCoordinateFocus}
                            onCoordinateBlur={handleCoordinateBlur}
                            sx={compositionMediaModulePanelInCardSx}
                        />
                    </Box>
                </Box>
            </Box>
            <Dialog
                open={Boolean(coordinateConfirm)}
                onClose={handleCoordinateConfirmCancel}
            >
                <DialogTitle sx={{textAlign: "center"}}>
                    Вы подтверждаете изменение?
                </DialogTitle>
                <DialogActions sx={{p: "1.25rem"}}>
                    <ButtonExit
                        text="Нет"
                        onClick={handleCoordinateConfirmCancel}
                        disabled={coordinateConfirmLoading}
                    />
                    <ButtonAdd
                        text="Да"
                        loading={coordinateConfirmLoading}
                        onClick={handleCoordinateConfirmSubmit}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CompositionSettingsMedia;
